import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiAlertCircle } from 'react-icons/fi';
import { departmentService, userService } from '../../services/api';
import './Admin.css';

const DepartmentManagement = () => {
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [deletingDept, setDeletingDept] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [deptRes, empRes] = await Promise.all([
        departmentService.getAll({ limit: 1000 }),
        userService.getAll({ limit: 1000 }),
      ]);
      setDepartments(deptRes.data || []);
      setEmployees(empRes.data.users || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeCount = (deptId) => {
    return employees.filter(e => e.departmentId === deptId).length;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Department name is required';
    }
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenForm = (dept = null) => {
    if (dept) {
      setEditingDept(dept);
      setFormData({ name: dept.name, description: dept.description || '' });
    } else {
      setEditingDept(null);
      setFormData({ name: '', description: '' });
    }
    setFormErrors({});
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingDept(null);
    setFormData({ name: '', description: '' });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSaving(true);
      if (editingDept) {
        await departmentService.update(editingDept.id, formData);
      } else {
        await departmentService.create(formData);
      }
      await fetchData();
      handleCloseForm();
    } catch (err) {
      setFormErrors({
        submit: err.response?.data?.message || 'Failed to save department',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingDept) return;

    try {
      setSaving(true);
      await departmentService.delete(deletingDept.id);
      await fetchData();
      setDeletingDept(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete department');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading departments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Department Management</h1>
        <button className="btn btn-primary" onClick={() => handleOpenForm()}>
          <FiPlus /> Add Department
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="department-grid">
        {departments.length === 0 ? (
          <div className="no-results">
            <p>No departments found. Create one to get started.</p>
          </div>
        ) : (
          departments.map(dept => (
            <div key={dept.id} className="department-card">
              <div className="card-header">
                <h3>{dept.name}</h3>
              </div>

              <p className="card-description">{dept.description || 'No description'}</p>

              <div className="card-stats">
                <span className="stat-badge">
                  {getEmployeeCount(dept.id)} Employee{getEmployeeCount(dept.id) !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="card-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => handleOpenForm(dept)}
                >
                  <FiEdit2 /> Edit
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => setDeletingDept(dept)}
                  disabled={getEmployeeCount(dept.id) > 0}
                  title={
                    getEmployeeCount(dept.id) > 0
                      ? 'Cannot delete department with employees'
                      : 'Delete department'
                  }
                >
                  <FiTrash2 /> Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={handleCloseForm}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingDept ? 'Edit Department' : 'Add Department'}</h2>
            </div>

            {formErrors.submit && <div className="error-banner">{formErrors.submit}</div>}

            <form onSubmit={handleSubmit} className="department-form">
              <div className="form-group">
                <label htmlFor="name">Department Name *</label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, name: e.target.value }));
                    if (formErrors.name) {
                      setFormErrors(prev => ({ ...prev, name: '' }));
                    }
                  }}
                  className={formErrors.name ? 'input-error' : ''}
                />
                {formErrors.name && <span className="error-message">{formErrors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseForm}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? (
                    <>
                      <div className="inline-spinner"></div> Saving...
                    </>
                  ) : (
                    <>
                      <FiPlus /> Save Department
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingDept && (
        <div className="modal-overlay" onClick={() => setDeletingDept(null)}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Department</h2>
            </div>

            <div className="modal-body">
              <div className="confirmation-icon">
                <FiAlertCircle size={48} />
              </div>
              <p className="confirmation-text">
                Are you sure you want to delete "{deletingDept.name}"? This action cannot be undone.
              </p>
            </div>

            <div className="form-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setDeletingDept(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="inline-spinner"></div> Deleting...
                  </>
                ) : (
                  'Delete Department'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentManagement;
